import React from 'react';

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

import { useThrottledToast } from '../../hooks/useThrottledToast';

import { Switch, PageHeader, Heading } from '../../shared/ui';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { selectUser, setUserData } from '../../store/auth.slice';
import { useChangeUserMutation } from '../../services/users.api';
import { useTariffToolCheck } from '../../hooks/useTariffToolCheck';

const SAND_THERAPY_MARKDOWN = `
**Песочная терапия** — это мощный проективный метод, позволяющий «говорить» на языке образов и символов. Мы перенесли его в онлайн-формат, сохранив все ключевые механизмы исцеления: безопасное пространство, свободу самовыражения и возможность увидеть свою внутреннюю ситуацию со стороны.

*Даже без физического песка вы сможете строить, расставлять, менять и исследовать. Это терапия смыслов, а не только ощущений.*

## Как это работает на ZiNGiZi?

1. Вы открываете **цифровую песочницу** с возможностью выбора цвета фона (от спокойного бежевого до глубокого синего).
2. Используете **стартовый набор из 20 фигур**: дома, деревья, люди, домашние животные — архетипический минимум для создания любой метафоры.
3. Расставляете их, перемещаете, убираете, меняете композицию столько раз, сколько нужно.
4. Фиксируете результат и вместе с психологом (или самостоятельно) анализируете пространство.

## Преимущества песочной терапии онлайн на ZiNGiZi

### 1. Полная свобода экспериментов без страха «испортить»

В физическом песке бывает жалко разрушать созданное. У нас вы можете **бесконечно менять композицию** — убрать фигуру одним кликом, переместить дом в другой угол, «стереть» конфликтную сцену. Это снижает перфекционизм и страх ошибки.

### 2. Мгновенная смена контекста через цвет песка

Физический песок один. У нас вы выбираете цвет:

- **Бежевый** — для ресурса, спокойствия, нейтральной основы.
- **Синий (вода/небо)** — для работы с эмоциями, подсознанием, страхами глубины.
- **Черный** — для сильных травм, утрат, теневых аспектов.
- **Зеленый** — для роста, исцеления, семейных систем.

Это дает **дополнительный диагностический инструмент**, которого нет у классической песочницы.

### 3. Нет ограничений «на одного клиента»

В офлайн-кабинете нужно чистить песок и мыть фигурки. У нас каждый клиент получает **чистый цифровой лист** с уникальным набором фигур. Никакой проекции на «чужой след» в песке.

### 4. Идеально для застенчивых и избегающих

Онлайн-формат снижает барьер входа. Многие клиенты (особенно подростки и взрослые с социальной тревогой) легче раскрываются, когда камера не фокусируется на их руках в песке, а видно только финальную или промежуточную композицию.

### 5. Удобная расстановка - фокус на отношениях между элементами

Отсутствие физического закапывания компенсируется **акцентом на пространственные отношения**: кто рядом с кем, кто выше, кто в центре. Это проясняет некоторые конфликты (например, нельзя «спрятать проблему в песок» — её придется предъявить или убрать осознанно).

## С какими запросами можно работать в таком формате?

Несмотря на отсутствие тактильности, метод эффективен для:

### Базовые эмоциональные запросы

- **«Каша в голове»** — структурирование мыслей через расстановку фигур.
- **Подавленная агрессия** — выставить «врагов» и найти способ их нейтрализовать (например, поставить их в угол).
- **Тревога и страхи** — материализовать «монстра» в виде фигурки и изменить его позу/местоположение.

### Отношения и коммуникация (взрослые и дети)

- **Семейные расстановки** (кто в семье на каком расстоянии, есть ли «лишний» человек).
- **Конфликт с коллегой/начальником** — кого поставить между вами, чтобы стало легче.
- **Ревность, одиночество, ощущение «меня не слышат»**.

### Внутренние конфликты («часть меня хочет, часть боится»)

- Представьте одну часть «домашним животным», а другую — «деревом». Посмотрите, как они расположены. Можно ли построить между ними мост (подвинуть фигуры)?

### Детские запросы (от 5 лет, совместно с родителем или терапевтом)

- Адаптация к школе/саду (построить «мир класса»).
- Страх темноты, врачей, чудовищ (поставить чудовище в угол и закрыть его домиком).
- Трудности с объяснением чувств («покажи, почему тебе грустно»).

### Потери и расставания

- **Утрата близкого** (поставить фигурку, символизирующую ушедшего, и найти для неё «место памяти» рядом с живыми). Закапывание не нужно — важна дистанция и взгляд.

### Кризисы и выбор пути

- **«Куда идти жить/работать?»** — поставьте домики в разных углах песочницы, а себя — в виде фигурки человека или животного. Куда тянется взгляд?

## Важное отличие от физической песочницы (и почему это не минус)

| Физическая песочница | ZiNGiZi онлайн |
| --- | --- |
| Тактильность успокаивает | Цвет песка + визуальная эстетика настраивают быстрее |
| Можно закопать «боль» | Боль приходится **предъявить и осознанно разместить** — это более честная работа |
| Фигурки на ощупь разные | 20 базовых архетипов достаточно для 90% запросов — меньше отвлекающих факторов |

## Кому особенно подойдет онлайн-формат?

- Тем, у кого нет возможности посещать очного психолога.
- Подросткам и молодым взрослым (цифровая среда для них родная).
- Клиентам с РПП, ОКР или сильным контролем — физический песок может вызывать брезгливость или перфекционизм. Цифровой песок безопасен.
- Для быстрых диагностических сессий (15–20 минут) — например, «покажи свой внутренний мир прямо сейчас».

## Резюме для психолога (шпаргалка)

**ZiNGiZi-песочница** — это **новая оптика**. Без закапывания мы учим клиента не прятать проблему, а осознанно с ней обращаться. Без тактильности мы тренируем визуальную метафору и гибкость мышления. А возможность менять цвет песка и возвращаться к композиции спустя время даёт то, чего нет в офлайне.
`;

const SandTherapy = () => {
  const tool = 'sand_therapy';
  const isActiveByTariff = useTariffToolCheck(tool);
  const dispatch = useAppDispatch();
  const userData = useAppSelector(selectUser);
  const [changeUser] = useChangeUserMutation();
  const showToast = useThrottledToast();

  const handleCheck = async () => {
    try {
      if (!isActiveByTariff) return;
      if (!userData) return;
      if (userData?.active_psy_tools?.includes(tool)) {
        const arr = userData?.active_psy_tools.filter((el) => el !== tool);
        dispatch(setUserData({ ...userData, active_psy_tools: arr }));
        await changeUser({ id: userData.id, active_psy_tools: arr });
      } else {
        dispatch(setUserData({ ...userData, active_psy_tools: [...userData?.active_psy_tools, tool] }));
        await changeUser({ id: userData.id, active_psy_tools: [...userData?.active_psy_tools, tool] });
      }
      showToast('success');
    } catch {
      showToast('error');
    }
  };

  return (
    <div className="pb-[80px]">
      <PageHeader backTo="/tools" title="Песочная терапия" />

      <div className="w-full mb-10">
        <div className="text-ink text-ui leading-relaxed">
          <Heading size="md" className="mb-4">
            Песочная терапия онлайн на ZiNGiZi: создай свой мир, найди решение
          </Heading>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={{
              h2: ({ children }) => (
                <Heading size="md" className="mt-8 mb-4 first:mt-0">
                  {children}
                </Heading>
              ),
              h3: ({ children }) => (
                <Heading size="sm" as="h3" className="mt-6 mb-3">
                  {children}
                </Heading>
              ),
              p: (props) => <p className="mb-4 last:mb-0 text-ink-muted" {...props} />,
              ul: (props) => <ul className="list-disc pl-6 mb-4 space-y-2" {...props} />,
              ol: (props) => <ol className="list-decimal pl-6 mb-4 space-y-2" {...props} />,
              li: (props) => <li className="text-ink-muted" {...props} />,
              hr: () => <hr className="my-8 border-t border-ink-muted" />,
              strong: (props) => <strong className="font-medium text-ink" {...props} />,
              table: (props) => (
                <div className="my-6 w-full">
                  <table className="w-full max-w-4xl border border-line text-ui border-collapse" {...props} />
                </div>
              ),
              thead: (props) => <thead className="bg-surface-muted" {...props} />,
              th: ({ children, ...props }) => (
                <th
                  className="border border-line px-4 py-3 text-left align-top font-medium whitespace-nowrap text-ink"
                  {...props}
                >
                  {children}
                </th>
              ),
              td: (props) => (
                <td className="border border-line px-4 py-3 align-top leading-relaxed text-ink-muted" {...props} />
              ),
            }}
          >
            {SAND_THERAPY_MARKDOWN}
          </ReactMarkdown>
        </div>
      </div>

      <div className="flex items-center gap-5 mb-5">
        <Switch
          checked={userData?.active_psy_tools?.includes(tool)}
          disabled={!isActiveByTariff}
          onChange={() => handleCheck()}
          label={'Использовать Песочную терапию в видео-сессиях'}
        />
      </div>
    </div>
  );
};

export default SandTherapy;
